<template>
  <Layout title="" description="">
    <div class="px-4 sm:px-0">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Job Management</h1>
          <p class="mt-2 text-gray-600">Manage Job assignments and bookings</p>
        </div>
        <div class="flex flex-col sm:flex-row gap-2 mt-4 sm:mt-0">
          <input v-model="selectedDate" type="date" class="form-control" @change="fetchBookingsByDate" />
        </div>
      </div>

      <p class="text-sm text-gray-500 mt-2">
        These are bookings for the selected date. Click “Edit” to assign drivers, upload documents, add remarks, or change status.
      </p>

      <!-- Search -->
      <div class="mb-6">
        <div class="relative">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            v-model="searchTerm"
            placeholder="Search bookings..."
            class="w-full pl-10 pr-4 py-2 h-10 border rounded text-sm"
          />
        </div>
      </div>

      <!-- Bookings List -->
      <div class="grid gap-6">
        <div
            v-for="(b, idx) in filteredBookings"
            :key="idx"
            class="card bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
          >
            <!-- Header Row -->
            <div
              class="flex justify-between items-center px-6 py-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
              @click="toggleExpand(b.id)"
            >
              <div>
                <h2 class="text-lg font-semibold text-gray-900">
                  {{ b.id }} — {{ b.vesselName || 'No Vessel' }} ({{ b.counterNumber || 'No Counter' }})
                </h2>
                <p class="text-sm text-gray-600">
                  {{ getDisplayStatus(b.status) }} • {{ formatDT(b.startTime) }}
                </p>
                <p class="text-sm text-indigo-600 font-medium">
                  Agent: {{ getClientName(b.clientId) || '—' }}
                </p>
                <p class="text-sm text-gray-600">
                  Flight: {{ displayFlightNumbers(b.flightNumbers) || '—' }}
                </p>

              </div>
              <div class="flex items-center gap-2">
                <!-- <button
                  class="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  @click.stop="forceAssignDriver(b)"
                >
                  <UserCheck class="w-4 h-4" />
                  Force Assign
                </button> -->
                <!-- <button
                  class="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  @click.stop="openEdit(b)"
                >
                  <Edit3 class="w-4 h-4" />
                  Edit
                </button> -->

                  <!-- <button
                    class="inline-flex items-center gap-1.5 text-green-600 hover:text-green-800 text-sm font-medium"
                    @click.stop="downloadAllJobFiles(b)"
                  >
                    <Download class="w-4 h-4" />
                    Download 
                  </button> -->

                <span :class="'badge ' + getStatusColor(b.status)">
                  {{ getDisplayStatus(b.status) }}
                </span>

                <svg
                  :class="['w-5 h-5 transition-transform', expandedCards[b.id] ? 'rotate-180' : 'rotate-0']"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          

            <!-- Collapsible Body -->
            <transition name="fade">
              <div v-if="expandedCards[b.id]" class="px-6 py-4 border-t border-gray-200 bg-white">
                <!-- 🔹 Everything below stays the same as your full content -->
                <!-- Start/End -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 class="font-medium text-gray-900 mb-1">Start Time</h4>
                    <p class="text-sm text-gray-600">{{ formatDT(b.startTime) }}</p>
                  </div>
                  <div>
                    <h4 class="font-medium text-gray-900 mb-1">Pax</h4>
                    <p class="text-sm text-gray-600">{{ b.pax }}</p>
                  </div>
                </div>
                


                <!-- Start/End Location -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 class="font-medium text-gray-900 mb-1">Start Location</h4>
                    <p class="text-sm text-gray-600">{{ b.startLocation || '—' }}</p>
                  </div>
                  <div>
                    <h4 class="font-medium text-gray-900 mb-1">End Location</h4>
                    <p class="text-sm text-gray-600">{{ b.endLocation || '—' }}</p>
                  </div>
                </div>
                <!-- Crew Name -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 class="font-medium text-gray-900 mb-1">Crew Name</h4>
                    <p class="text-sm text-gray-600">
                      {{ getCrewDisplayName(b) }}
                    </p>
                  </div>
                  <div>
                    <h4 class="font-medium text-gray-900 mb-1">Booking Type</h4>
                    <p class="text-sm text-gray-600">
                      {{ b.bookingType || '—'  }}
                    </p>

                  </div>
                </div>

                <!-- Trips -->
                <div v-if="b.trips && b.trips.length" class="mb-4">
                  <h4 class="font-medium text-gray-900 mb-1">Trips</h4>
                  <ul class="list-disc pl-5 text-sm text-gray-600">
                    <li v-for="(trip, i) in b.trips" :key="i">{{ trip }}</li>
                  </ul>
                </div>

                <!-- Drivers -->
                <!-- <div class="mb-3">
                  <h4 class="font-medium text-gray-900 mb-2">Assigned Drivers</h4>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="(name, idx) in mapDriverIdsToNames(b.driverIDs)"
                      :key="idx"
                      class="badge-secondary"
                    >
                      {{ name }}
                    </span>
                    <span v-if="!b.driverIDs || b.driverIDs.length === 0" class="text-sm text-gray-500">
                      No drivers assigned
                    </span>
                  </div>
                </div> -->

                <!-- Remarks -->
                <div class="mb-4">
                  <h4 class="font-medium text-gray-900 mb-1">Remarks</h4>
                  <p class="text-sm text-gray-600 whitespace-pre-wrap">{{ b.remarks || '—' }}</p>
                </div>
                
                <div
                    class="mt-4"
                    v-if="b.images_proof && b.images_proof.length"
                  >
                    <h6 class="fw-bold mb-3">Uploaded Proofs</h6>

                    <div class="row g-3">
                      <div
                        v-for="(img, i) in b.images_proof"
                        :key="i"
                        class="col-md-4"
                      >
                        <div class="proof-card">
                          <img
                            v-if="img.previewUrl"
                            :src="img.previewUrl"
                            class="proof-img"
                          />

                          <div class="proof-info">
                            {{ img.uploadedAt }}  
                            <br />
                            <strong>
                              {{ img.isOnboard ? 'Onboard' : 'Completed' }}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                

                <!-- ✅ Status Timeline (same clean version) -->
                <div v-if="b.statusTimeline && b.statusTimeline.length" class="col-12 mt-4">
                  <div class="flex justify-between items-center mb-2">
                    <label class="form-label fw-semibold mb-0">Status Timeline</label>
                    <!-- <button
                      class="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium"
                      @click="openTimelineEdit(b)"
                    >
                      <Edit3 class="w-4 h-4" />
                      Edit Timeline
                    </button> -->
                  </div>
                  <p class="text-sm text-gray-500 mb-2">
                    Below shows the recorded timestamps for each job status.
                  </p>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div
                      v-for="(s, i) in sortStatusTimeline(b.statusTimeline)"
                      :key="i"
                      class="border rounded p-3 bg-gray-50 shadow-sm"
                    >
                      <label class="block text-sm font-medium text-gray-700 mb-1">
                        {{ s.status }}
                      </label>
                      <p class="text-sm text-gray-600">
                        {{ s.time ? formatDate(s.time) : '—' }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>

      </div>
      
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import { mapGetters } from 'vuex'
import { Search } from 'lucide-vue-next'
import axios from 'axios'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import router from '@/router'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import 'vue3-timepicker/dist/VueTimepicker.css'
import { useToast } from 'vue-toastification'

export default {
  name: 'JobManagement',
  components: { Layout, Search },
  data() {
    return {

      selectedDate: '',
      searchTerm: '',
      bookings: [],
      drivers: [],
      clients: [],
      isEditOpen: false,
      editBooking: null,
      uploadedFiles: [{ file: null }],
      isSaving: false,
      expandedCards: {}, // ✅ ADD THIS
      editForm: {
        status: 'New',
        complimentary: false,
        startDate: '',
        startTime: '',
        driverIDs: [],
        flightNumbers: [''],
        trips: [''],
        clientId: '',
        createdBy: '',
        remarks: '',
        pax: '',
        startLocation: '',
        endLocation: '',
        vesselName: '',
        counterNumber: ''
      },
      isTimelineEditOpen: false,
      currentBookingId: '',
      timelineEdit: []
    }
  },
  computed: {
    ...mapGetters(['getSystemAccess']),
    canEdit() {
      const access = this.getSystemAccess || {}
      return access?.admin === true || access?.driver === true
    },
    filteredBookings() {
      const q = (this.searchTerm || '').toLowerCase().trim()
      if (!q) return this.bookings
      return this.bookings.filter(b =>
        [b.id, b.startLocation, b.endLocation, b.status, b.vesselName, ...(this.mapDriverIdsToNames(b.driverIDs))].some(v =>
          String(v || '').toLowerCase().includes(q)
        )
      )
    }
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  methods: {
    // ===== AUTH =====
    async getAuthHeader() {
      const user = getAuth().currentUser
      if (!user) throw new Error('Not logged in')
      const token = await user.getIdToken()
      return { Authorization: token }
    },
    async checkAuthState() {
      const auth = getAuth()
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async user => {
          if (user) {
            this.token = await user.getIdToken()
            resolve()
          } else {
            router.push('/')
            reject(new Error('No user logged in'))
          }
        })
      })
    },
    getCrewDisplayName(b) {

      // V1 priority (legacy)
      if (b.crewName && b.crewName.trim() !== '') {
        return b.crewName
      }

      // V2
      if (Array.isArray(b.crewMembers) && b.crewMembers.length > 0) {

        const formatted = b.crewMembers
          .map(c => {
            const rank = (c?.rank || '').trim()
            const name = (c?.name || '').trim()

            if (!name) return null

            // Rank required, but just in case old data exists
            return rank ? `${rank} ${name}` : name
          })
          .filter(Boolean)

        if (formatted.length > 0) {
          return formatted.join(', ')
        }
      }

      return '—'
    },
    normalizeStatusTimeline(timelineObj = {}) {
      const MAP = {
        new: 'New',
        on_route: 'On_Route',
        arrived: 'Arrived',
        on_board: 'On_Board',
        completed: 'Completed',
        dop: 'Completed',
        failure: 'Cancelled',
        cancelled: 'Cancelled'
      }

      const ORDER = [
        'New',
        'On_Route',
        'Arrived',
        'On_Board',
        'Completed',
        'Cancelled'
      ]

      const normalized = {}

      Object.entries(timelineObj || {}).forEach(([key, val]) => {
        const status = MAP[key.toLowerCase()]
        if (!status) return
        normalized[status] = val?.at || 0
      })

      return ORDER.map(st => ({
        status: st,
        time: normalized[st] || 0
      }))
    },
    
    checkPageAccess(allowedRoles = []) {
      const access = this.$store.state.systemAccess || {}

      const hasAccess = allowedRoles.some(role => access[role] === true)

      if (!hasAccess) {
        this.toast.error("Access denied.")

        if (access.admin === true) {
          router.push("/crm")
        } else if (access.client === true) {
          router.push("/JobManagementClient")
        } else if (access.driver === true) {
          router.push("/JobManagementDriver")
        } else {
          router.push("/")
        }
      }
    },
    editVoucher(voucherId) {
      this.$router.push(`/AdminVoucher/${voucherId}`)
    },
    async deleteVoucher(voucherId) {
      const access = this.$store.state.systemAccess || {}
      if (!access.admin) {
        this.toast.error("Access Denied")
        return
      }

      try {
        const headers = await this.getAuthHeader()
        const res = await axios.delete(`${this.$apiURL}/api/vouchers/${voucherId}`, { headers })
        if (res?.data?.ok) {
          this.toast.success('Voucher deleted successfully!')
          await this.fetchBookingsByDateFive?.()
        } else {
          this.toast.error(res?.data?.error || 'Failed to delete voucher.')
        }
      } catch (err) {
        const msg = err?.response?.data?.error || err.message || 'Error deleting voucher.'
        this.toast.error(msg)
      }
    },

    // ===== FORMATTERS =====
    formatDate(ts) {
      if (!ts || ts === 0) return '-' // 👈 prevent 1970

      try {
        if (typeof ts === 'object' && (ts._seconds || ts.seconds)) {
          const sec = ts._seconds || ts.seconds
          const d = new Date(sec * 1000)
          return d.toLocaleString('en-SG', { hour12: false })
        }

        const d = new Date(ts)
        if (isNaN(d.getTime())) return '-'
        return d.toLocaleString('en-SG', { hour12: false })
      } catch {
        return '-'
      }
    },
    formatDT(val) {
      if (!val) return '—'
      try {
        const d = new Date(val)
        if (isNaN(d.getTime())) return val
        return d.toLocaleString('en-SG', { hour12: false })
      } catch {
        return val
      }
    },
    normalizeFlights(input) {
      try {
        let arr = input
        if (typeof arr === "string") arr = JSON.parse(arr)
        if (!Array.isArray(arr)) arr = []

        arr = arr.map(o => ({
          flight: o.flight || o.number || "",
          IsArrival: o.IsArrival ?? o.isArrival ?? false,
          IsDeparture: o.IsDeparture ?? o.isDeparture ?? false,
        }))

        if (arr.length === 0) arr = [{ flight: "", IsArrival: false, IsDeparture: false }]
        return arr
      } catch (e) {
        console.warn("normalizeFlights failed:", input, e)
        return [{ flight: "", IsArrival: false, IsDeparture: false }]
      }
    },

    serializeFlights(arr) {
      return JSON.stringify(
        (Array.isArray(arr) ? arr : [])
          .map(o => ({
            number: String(o.flight || "").trim(),
            isArrival: !!o.IsArrival,
            isDeparture: !!o.IsDeparture,
          }))
          .filter(o => o.number !== "")
      )
    },


    getStatusColor(status) {
      switch ((status || '').toLowerCase()) {
        case 'new':
          return 'bg-blue-100 text-blue-800'
        case 'assigned':
          return 'bg-indigo-100 text-indigo-800'
        case 'assigning':
          return 'bg-purple-100 text-purple-800'
        case 'on route':
          return 'bg-yellow-100 text-yellow-800'
        case 'arrived':
          return 'bg-amber-100 text-amber-800'
        case 'on board':
          return 'bg-teal-100 text-teal-800'
        case 'dop':
        case 'completed':
          return 'bg-green-100 text-green-800'
        case 'cancelled':
          return 'bg-red-100 text-red-800'
        default:
          return 'bg-gray-100 text-gray-800'
        
      }
    },
    getDisplayStatus(status) {
      if (!status) return '—'
      switch (status.toLowerCase()) {
        case 'failure':
          return 'cancelled'
        case 'dop':
          return 'Completed'
        default:
          return status
      }
    },
    mapDriverIdsToNames(ids) {
      if (!Array.isArray(ids) || !ids.length) return []
      const map = new Map(this.drivers.map(d => [d.id, d.name || 'Unnamed']))
      return ids.map(id => {
        if (id === 'OUTSOURCE') return 'Outsourced'
        return map.get(id) || 'Unknown'
      })
    },
    toLocalInputValue(isoString) {
      try {
        // 🔹 Ensure we treat timestamp as SG time (not UTC)
        const d = new Date(isoString.includes('+') ? isoString : `${isoString}+08:00`);
        const pad = n => String(n).padStart(2, '0');
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const hh = pad(d.getHours());
        const mi = pad(d.getMinutes());
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
      } catch {
        return '';
      }
    },
    fromLocalToISO(localVal) {
      try {
        if (!localVal) return null;

        // ⚙️ Parse local date/time manually, don't let JS convert it to UTC
        const [datePart, timePart] = localVal.split("T");
        const [year, month, day] = datePart.split("-").map(Number);
        const [hour, minute, second] = (timePart || "00:00:00").split(":").map(Number);

        // ✅ Build ISO manually with +08:00 timezone (Singapore)
        return `${year.toString().padStart(4, "0")}-${month
          .toString()
          .padStart(2, "0")}-${day.toString().padStart(2, "0")}T${hour
          .toString()
          .padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}:${(second || 0)
          .toString()
          .padStart(2, "0")}+08:00`;
      } catch (err) {
        console.warn("fromLocalToISO error:", localVal, err);
        return null;
      }
    },

    // ===== DISPLAY HELPERS =====
    displayFlightNumbers(fns) {
      try {
        if (Array.isArray(fns)) {
          if (fns.length && typeof fns[0] === 'object') {
            return fns
              .map(o => o?.number || '')
              .filter(Boolean)
              .join(', ')
          }
          return fns.join(', ')
        }

        if (typeof fns === 'string') {
          const parsed = JSON.parse(fns)
          return this.displayFlightNumbers(parsed)
        }

        return ''
      } catch (e) {
        console.warn('⚠️ Failed to display flight numbers:', fns, e)
        return ''
      }
    },


    // ===== FETCH =====
    async fetchBookingsByDate() {
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.get(`${this.$apiURL}/api/driver/bookings`, {
          headers,
          params: { date: this.selectedDate }
        })

        // 🔹 Normalize and mock vouchers
        this.bookings = (res.data?.bookings || []).map(b => {
          b.statusTimeline = this.normalizeStatusTimeline(b.status_timeline)
          return b
        })

      } catch (err) {
        console.error("❌ fetchBookingsByDate error:", err)
        this.toast.error(err.response.data.error)
        this.bookings = []
      }
    },
    async fetchBookingsByDateFive() {
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.get(`${this.$apiURL}/api/driver/bookings/five`, {
          headers,
          params: { date: this.selectedDate }
        })

        // 🔹 Normalize and mock vouchers
        this.bookings = (res.data?.bookings || []).map(b => {
          b.statusTimeline = this.normalizeStatusTimeline(b.status_timeline)
          return b
        })

      } catch (err) {
        console.error("❌ fetchBookingsByDateFive error:", err)
        this.toast.error(err.response.data.error)
        this.bookings = []
      }
    },


    // async fetchDrivers() {
    //   try {
    //     const headers = await this.getAuthHeader()
    //     const res = await axios.get(`${this.$apiURL}/drivers`, { headers })
    //     this.drivers = res.data?.drivers || []
    //   } catch (err) {
    //     console.error('❌ fetchDrivers error:', err)
    //     this.drivers = []
    //   }
    // },

    async fetchClients() {
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.get(`${this.$apiURL}/clients`, { headers })
        this.clients = res.data?.clients || []
      } catch (err) {
        console.error('❌ fetchClients error:', err)
        this.clients = []
      }
    },

    
    extractDate(iso) {
      try {
        const d = new Date(iso.includes('+') ? iso : `${iso}+08:00`)
        const pad = n => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      } catch {
        return ''
      }
    },
    extractTime(iso) {
      try {
        const d = new Date(iso.includes('+') ? iso : `${iso}+08:00`);
        const pad = n => String(n).padStart(2, '0');
        return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch {
        return '';
      }
    },


    combineDateTimeToISO(date, time) {
      try {
        if (!date || !time) return null;
        const combined = `${date}T${time}:00`;
        const d = new Date(combined);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const hours = String(d.getHours()).padStart(2, "0");
        const mins = String(d.getMinutes()).padStart(2, "0");
        const secs = String(d.getSeconds()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${mins}:${secs}+08:00`;
      } catch {
        return null;
      }
    },
    
    getClientName(clientId) {
      if (!clientId) return ''
      if (String(clientId).toLowerCase() === 'admin') return 'Admin' // ✅ handle admin
      const c = this.clients.find(cl => cl.id === clientId)
      return c ? c.contactPerson || c.name || c.companyName || '' : ''
    },
   
    toggleExpand(id) {
      this.expandedCards = {
        ...this.expandedCards,
        [id]: !this.expandedCards[id]
      }
    },
    sortStatusTimeline(timeline) {
      const order = ['On_Board', 'Completed', 'Cancelled']

      // make lookup
      const lookup = {}
      timeline.forEach(s => (lookup[s.status] = s.time || 0))

      // rebuild full list in desired order, fill missing with '-'
      const fullList = order.map(status => ({
        status,
        time: lookup[status] || 0
      }))

      // find latest non-zero
      const latestStatus = fullList.reduce((latest, item) => {
        const t =
          typeof item.time === 'object' && item.time.seconds
            ? item.time.seconds * 1000
            : new Date(item.time).getTime()
        return t > (latest.time || 0) ? { ...item, time: t } : latest
      }, { time: 0 })

      return fullList.map(s => ({
        ...s,
        displayStatus: s.status === 'DOP' ? 'Completed' : s.status,
        isLatest: s.status === latestStatus.status && latestStatus.time > 0
      }))
    },
    openTimelineEdit(b) {
      this.currentBookingId = b.id;

      this.timelineEdit = this.sortStatusTimeline(b.statusTimeline).map(s => {
        if (!s.time || s.time === 0) return { ...s, date: '', time: '' };

        // interpret backend “GMT” as UTC then convert to Singapore
        const sg = dayjs.utc(s.time).tz('Asia/Singapore');
        const date = sg.format('YYYY-MM-DD');
        const time = sg.format('HH:mm');

        return { ...s, date, time };
      });

      this.isTimelineEditOpen = true;
    },


    closeTimelineEdit() {
      this.isTimelineEditOpen = false
      this.currentBookingId = ''
      this.timelineEdit = []
    },
    async saveTimelineEdit() {
      try {
        const headers = await this.getAuthHeader();

        const formatted = this.timelineEdit.map(s => {
          if (!s.date || !s.time) return { status: s.status, time: null };

          // Combine and convert to proper ISO +08:00
          const combined = `${s.date}T${s.time}`;
          return {
            status: s.status,
            time: this.fromLocalToISO(combined)
          };
        });

        const res = await axios.put(
          `${this.$apiURL}/api/bookings/${this.currentBookingId}/timeline`,
          { timeline: formatted },
          { headers }
        );

        if (res.data.ok) {
          this.toast.success('✅ Timeline updated!');
          this.closeTimelineEdit();
          await this.fetchBookingsByDate();
        } else {
          this.toast.error('❌ Failed to update timeline.');
        }
      } catch (err) {
        console.error('❌ saveTimelineEdit error:', err);
        this.toast.error('Error updating timeline.');
      }
    },
    async forceAssignDriver() {
    return
    }
    




  },

  async mounted() {
    dayjs.extend(utc)
    dayjs.extend(timezone)
    const sgNow = dayjs().tz('Asia/Singapore')
    this.selectedDate = sgNow.format('YYYY-MM-DD')
    await this.checkAuthState()
    await this.checkPageAccess(["driver"])
    // await this.fetchDrivers()
    await this.fetchClients()
    // await this.fetchBookingsByDate()
    await this.fetchBookingsByDateFive()
  }
}
</script>

<style scoped>
.badge {
  display: inline-block;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
}
.badge-secondary {
  background: #eef2ff;
  color: #3730a3;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
}
.btn-outline-primary.btn-sm,
.btn-outline-danger.btn-sm {
  margin-top: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}
.form-control[type='file'] {
  font-size: 0.9rem;
}
.modal-content {
  border-radius: 0.75rem;
  overflow: hidden;
}
.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
}
.modal-footer {
  border-top: 1px solid #e5e7eb;
  background-color: #fafafa;
}
.btn-close {
  background: transparent;
  border: none;
}
.text-danger {
  color: #dc3545 !important;
}
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
.card {
  border: 1px solid #e5e7eb;
}
.card-header {
  border-bottom: 1px solid #e5e7eb;
}
.card-content {
  background-color: #fff;
}
.whitespace-pre-wrap {
  white-space: pre-wrap;
}
.vue-timepicker-wrapper {
  width: 100%;
}

/* make its internal input match Bootstrap form-control */
:deep(.vue__time-picker input) {
  display: block;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

:deep(.vue__time-picker input:focus) {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13,110,253,0.25);
}
img {
  max-height: 500px;
  object-fit: contain;
}
.proof-card {
  background: #fff;
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  text-align: center;
}

.proof-img {
  width: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.proof-info {
  font-size: 12px;
  margin-top: 6px;
  color: #666;
}

</style>
